import { AfterViewInit, Component, ElementRef, Inject, Injector, OnDestroy, PLATFORM_ID, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { AboutIndustryItem, AboutPillarItem, AboutReasonItem, AboutStatItem } from './about.models';
import { AboutFinalCtaSectionComponent } from './sections/about-final-cta-section/about-final-cta-section.component';
import { AboutHeroSectionComponent } from './sections/about-hero-section/about-hero-section.component';
import { AboutIndustriesSectionComponent } from './sections/about-industries-section/about-industries-section.component';
import { AboutPillarsSectionComponent } from './sections/about-pillars-section/about-pillars-section.component';
import { AboutUniqueSectionComponent } from './sections/about-unique-section/about-unique-section.component';
import { AboutWhySectionComponent } from './sections/about-why-section/about-why-section.component';
import { runAfterHydration } from '../shared/run-after-hydration';
import { initPageScrollReveal } from '../shared/scroll-reveal.util';

@Component({
  selector: 'app-about',
  standalone: true,
  imports: [
    CommonModule,
    AboutFinalCtaSectionComponent,
    AboutHeroSectionComponent,
    AboutIndustriesSectionComponent,
    AboutPillarsSectionComponent,
    AboutUniqueSectionComponent,
    AboutWhySectionComponent
  ],
  templateUrl: './about.component.html',
  styleUrl: './about.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class AboutComponent implements AfterViewInit, OnDestroy {
  @ViewChild('aboutRoot') aboutRoot!: ElementRef<HTMLElement>;

  readonly aboutValues: string[] = [
    'Innovación constante',
    'Transparencia y ética',
    'Trabajo en equipo',
    'Resultados medibles',
  ];

  readonly uniqueStats: AboutStatItem[] = [
    {
      value: '360°',
      title: 'Estrategia integrada',
      description: 'Conectamos mensaje, canales y experiencia para que cada acción sume a un mismo objetivo.'
    },
    {
      value: 'ROI',
      title: 'Decisiones con datos',
      description: 'Métricas claras, reporting transparente y optimización continua para mejorar resultados.'
    },
    {
      value: 'Ágil',
      title: 'Ejecución en sprints',
      description: 'Iteramos rápido, entregamos seguido y coordinamos equipos multidisciplinarios sin fricción.'
    }
  ];

  readonly industries: AboutIndustryItem[] = [
    {
      icon: 'bi-cart-check',
      title: 'Ecommerce',
      description: 'Performance y optimización del funnel para crecer ventas, mejorar conversión y aumentar recurrencia.'
    },
    {
      icon: 'bi-bank',
      title: 'Finanzas',
      description: 'Comunicación clara y campañas medibles para marcas que requieren confianza, consistencia y precisión.'
    },
    {
      icon: 'bi-briefcase',
      title: 'Servicios Profesionales B2B',
      description: 'Generación de demanda, posicionamiento y contenido estratégico para ciclos de venta consultivos.'
    },
    {
      icon: 'bi-buildings',
      title: 'Inmobiliario',
      description: 'Captación de leads y campañas siempre activas, con seguimiento y medición para maximizar cierres.'
    },
    {
      icon: 'bi-camera-reels',
      title: 'Medios & Entretenimiento',
      description: 'Lanzamientos y comunicación con alto impacto, diseñados para amplificar audiencias y conversación.'
    },
    {
      icon: 'bi-cpu',
      title: 'Tecnología & Startups',
      description: 'Aceleramos posicionamiento y crecimiento con estrategia, performance y optimización continua.'
    }
  ];

  readonly reasons: AboutReasonItem[] = [
    { icon: 'bi-bullseye', kicker: '', title: 'Estrategia orientada a resultados' },
    { icon: 'bi-cpu', kicker: '', title: 'Tecnología y automatización integradas' },
    { icon: 'bi-diagram-3', kicker: '', title: 'Ejecución omnicanal' },
    { icon: 'bi-graph-up-arrow', kicker: '', title: 'Optimización continua basada en datos' }
  ];

  readonly pillars: AboutPillarItem[] = [
    {
      icon: 'bi-shield-check',
      title: 'Transparencia absoluta',
      description: 'Construimos relaciones basadas en claridad. Te mantenemos informado sobre métricas, avances, desafíos y oportunidades.'
    },
    {
      icon: 'bi-bullseye',
      title: 'Creatividad estratégica',
      description: 'La creatividad debe mover indicadores. Diseñamos ideas conectadas a objetivos concretos de negocio.'
    },
    {
      icon: 'bi-rocket',
      title: 'Agilidad y adaptación',
      description: 'El mundo digital cambia rápido. Iteramos, aprendemos y ajustamos para mantener ventaja competitiva.'
    },
    {
      icon: 'bi-puzzle',
      title: 'Cultura colaborativa',
      description: 'Integramos disciplinas, talento y visión estratégica para crear soluciones sólidas y accionables.'
    }
  ];

  private gsapCleanups: Array<() => void> = [];

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private injector: Injector
  ) {}

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      runAfterHydration(this.injector, () => void this.setupScrollRevealAnimation());
    }
  }

  ngOnDestroy(): void {
    this.gsapCleanups.forEach((cleanup) => cleanup());
    this.gsapCleanups = [];
  }

  private async setupScrollRevealAnimation(): Promise<void> {
    const root = this.aboutRoot?.nativeElement;
    if (!root) return;
    await initPageScrollReveal(root, this.gsapCleanups);
  }
}
